import React, { useMemo, useState } from "react";
import { AlertCircle, ArchiveRestore, CalendarClock, Clock, IndianRupee, Loader2, RefreshCw, RotateCcw, Search, ShieldAlert, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { EmptyState, KpiCard } from "./common";
import { PaginationBar, TableRowsSkeleton, useDebounced, usePagedList } from "./PaginationBar";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { api, errMsg, fmtDate, fmtDateTime, fmtINR } from "../lib/api";
import { pokeLiveCollections } from "../lib/liveCollections";
import { RESTORE_WINDOW_DAYS } from "./DeleteReceiptDialog";

const ROLE_LABEL = { super_admin: "Super Admin", municipal_admin: "Municipal Admin" };

const WindowBadge = ({ row, testId }) => row.expired ? (
  <Badge variant="outline" className="text-[10px] gap-1 bg-slate-100 text-slate-700 border-slate-200" data-testid={testId}>
    <Clock className="h-2.5 w-2.5" /> Window closed
  </Badge>
) : (
  <Badge variant="outline" className={`text-[10px] gap-1 ${row.days_left <= 5 ? "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30" : "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20"}`} data-testid={testId}>
    <CalendarClock className="h-2.5 w-2.5" /> {row.days_left} day{row.days_left === 1 ? "" : "s"} left
  </Badge>
);

/**
 * Phase 177 — "Deleted receipts" tab (Municipal Admin → Collections, and the Super Admin › Receipts page).
 *   path           list endpoint (tenant `/t/{code}/collections/deleted` or platform `/platform/deleted-receipts`)
 *   extraParams    e.g. {code} for the platform endpoint
 *   codeOf(row)    tenant code to call restore / purge against (platform rows carry row.municipality.code)
 *   showMunicipality   adds the Municipality column (Super Admin, all municipalities)
 */
export const DeletedReceiptsTab = ({ path, extraParams = {}, codeOf, showMunicipality = false, reloadKey = 0, testId = "deleted-receipts" }) => {
  const [q, setQ] = useState("");
  const search = useDebounced(q);
  const [state, setState] = useState("all");
  const [confirm, setConfirm] = useState(null);    // {kind: "restore" | "purge", row}
  const [busy, setBusy] = useState(false);
  const params = useMemo(() => ({ ...extraParams, search: search || undefined, state: state === "all" ? undefined : state }),
    [JSON.stringify(extraParams), search, state]); // eslint-disable-line react-hooks/exhaustive-deps
  const list = usePagedList(path, { params, initialLimit: 25, reloadKey });
  const counts = list.data?.counts || {};
  const access = list.data?.access || { is_super: true };

  const run = async () => {
    if (!confirm) return;
    const { kind, row } = confirm;
    const code = codeOf(row);
    setBusy(true);
    try {
      if (kind === "restore") {
        const r = await api.post(`/t/${code}/collections/receipts/${row.id}/restore`);
        toast.success(`Receipt ${r.data.receipt_number} restored`, { description: "It counts again in every list, total and the collector's credit." });
      } else {
        const r = await api.delete(`/t/${code}/collections/receipts/${row.id}/permanent`);
        toast.success(`Receipt ${r.data.receipt_number} permanently deleted`, { description: "An archive copy is kept for the audit trail only." });
      }
      pokeLiveCollections();
      setConfirm(null);
      list.reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const cols = 8 + (showMunicipality ? 1 : 0);

  return (
    <div className="space-y-4" data-testid={`${testId}-tab`}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid={`${testId}-summary`}>
        <KpiCard label="Deleted receipts" value={list.data ? (counts.total ?? 0) : "—"} icon={Trash2} testId={`${testId}-kpi-total`} sub={`soft-deleted, kept ${RESTORE_WINDOW_DAYS} days`} />
        <KpiCard label="Restorable" value={list.data ? (counts.restorable ?? 0) : "—"} icon={ArchiveRestore} testId={`${testId}-kpi-restorable`} sub="inside the 30-day window" />
        <KpiCard label="Window closed" value={list.data ? (counts.expired ?? 0) : "—"} icon={Clock} testId={`${testId}-kpi-expired`} sub="Super Admin may delete permanently" />
        <KpiCard label="Amount removed" value={list.data ? (counts.amount ?? 0) : "—"} money={!!list.data} icon={IndianRupee} testId={`${testId}-kpi-amount`} />
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-3 sm:p-4 flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9 h-9" placeholder="Search receipt no, PIN, reason, deleted by…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testId}-search-input`} />
          </div>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger className="w-44 h-9" data-testid={`${testId}-state-select`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All deleted</SelectItem>
              <SelectItem value="restorable">Restorable</SelectItem>
              <SelectItem value="expired">Window closed</SelectItem>
            </SelectContent>
          </Select>
          {(q || state !== "all") && (
            <Button variant="ghost" size="sm" className="h-9" onClick={() => { setQ(""); setState("all"); }} data-testid={`${testId}-reset-filters`}>
              <X className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
          )}
          <Button variant="outline" size="sm" className="h-9 ml-auto" onClick={() => list.reload()} disabled={list.loading} data-testid={`${testId}-refresh`}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${list.loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          {list.error && !list.data ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center" data-testid={`${testId}-error`}>
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-sm text-muted-foreground">{errMsg(list.error)}</p>
              <Button size="sm" variant="outline" onClick={() => list.reload()} data-testid={`${testId}-retry`}><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry</Button>
            </div>
          ) : !list.data ? <TableRowsSkeleton rows={6} cols={cols} testId={`${testId}-skeleton`} /> : list.items.length === 0 ? (
            <EmptyState title={q || state !== "all" ? "No deleted receipts match" : "No deleted receipts"}
              subtitle={q || state !== "all" ? "Try another search or reset the filters." : `Receipts deleted from the register appear here and can be restored for ${RESTORE_WINDOW_DAYS} days.`} />
          ) : (
            <TooltipProvider delayDuration={200}>
              <div className={`overflow-x-auto ${list.loading ? "opacity-60 transition-opacity" : ""}`}>
                <Table data-testid={`${testId}-table`}>
                  <TableHeader>
                    <TableRow>
                      {showMunicipality && <TableHead>Municipality</TableHead>}
                      <TableHead>Receipt</TableHead>
                      <TableHead>Property</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="hidden md:table-cell">Collected</TableHead>
                      <TableHead>Deleted</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Restore window</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.items.map((r) => (
                      <TableRow key={r.id} className={r.expired ? "bg-muted/30" : ""} data-testid={`${testId}-row-${r.id}`} data-state-window={r.expired ? "expired" : "restorable"}>
                        {showMunicipality && (
                          <TableCell className="text-xs">
                            <p className="font-medium">{r.municipality?.name || "—"}</p>
                            <p className="text-muted-foreground font-mono">{r.municipality?.code}</p>
                          </TableCell>
                        )}
                        <TableCell className="font-mono text-sm whitespace-nowrap" data-testid={`${testId}-receipt-${r.id}`}>
                          {r.receipt_display}
                          {r.entered_via === "import" && <Badge variant="outline" className="ml-1.5 text-[10px] font-normal bg-violet-50 text-violet-800 border-violet-200">Imported</Badge>}
                        </TableCell>
                        <TableCell>
                          <p className="font-mono text-sm">{r.pin || "—"}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[180px]">{r.owner_name || ""}{r.ward_label ? ` · ${r.ward_label}` : ""}</p>
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium whitespace-nowrap" data-testid={`${testId}-amount-${r.id}`}>{fmtINR(r.amount)}</TableCell>
                        <TableCell className="hidden md:table-cell text-xs">
                          <p className="tabular-nums">{fmtDate(r.payment_date)}</p>
                          <p className="text-muted-foreground truncate max-w-[160px]">{r.payment_mode || ""}{r.collected_by_name ? ` · ${r.collected_by_name}` : ""}</p>
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap" data-testid={`${testId}-deleted-${r.id}`}>
                          <p className="tabular-nums">{fmtDateTime(r.deleted_at)}</p>
                          <p className="text-muted-foreground truncate max-w-[160px]">{r.deleted_by?.name || r.deleted_by_name || "—"}{ROLE_LABEL[r.deleted_by?.role || r.deleted_by_role] ? ` · ${ROLE_LABEL[r.deleted_by?.role || r.deleted_by_role]}` : ""}</p>
                        </TableCell>
                        <TableCell className="text-xs max-w-[240px]">
                          <Tooltip>
                            <TooltipTrigger asChild><p className="truncate" data-testid={`${testId}-reason-${r.id}`}>{r.delete_reason || "—"}</p></TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[320px] text-xs">{r.delete_reason}</TooltipContent>
                          </Tooltip>
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          <WindowBadge row={r} testId={`${testId}-window-${r.id}`} />
                          <p className="text-muted-foreground mt-0.5">until {fmtDate(r.restore_until)}</p>
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          {r.can_restore && (
                            <Button size="sm" variant="outline" className="h-8" onClick={() => setConfirm({ kind: "restore", row: r })} data-testid={`${testId}-restore-${r.id}`}>
                              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                            </Button>
                          )}
                          {r.can_purge && (
                            <Button size="sm" variant="destructive" className="h-8 ml-1.5" onClick={() => setConfirm({ kind: "purge", row: r })} data-testid={`${testId}-purge-${r.id}`}>
                              <ShieldAlert className="h-3.5 w-3.5 mr-1" /> Delete permanently
                            </Button>
                          )}
                          {!r.can_restore && !r.can_purge && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-xs text-muted-foreground" data-testid={`${testId}-noaction-${r.id}`}>
                                  {r.expired ? (access.is_super ? "—" : "Super Admin only") : "View only"}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="text-xs max-w-[260px]">
                                {r.expired ? `The ${RESTORE_WINDOW_DAYS}-day restore window has closed — only the Super Admin can permanently delete this receipt.`
                                  : "Your 'Delete receipts' access is View only — ask the Super Admin for full access to restore."}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="px-4 pb-3 border-t">
                <PaginationBar page={list.page} limit={list.limit} total={list.total} loading={list.loading}
                  onPageChange={list.setPage} onLimitChange={list.setLimit} testId={`${testId}-pagination`} />
              </div>
            </TooltipProvider>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!confirm} onOpenChange={(v) => !busy && !v && setConfirm(null)}>
        <DialogContent className="sm:max-w-md" data-testid={`${testId}-confirm-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              {confirm?.kind === "purge" ? <ShieldAlert className="h-5 w-5 text-destructive" /> : <RotateCcw className="h-5 w-5 text-primary" />}
              {confirm?.kind === "purge" ? "Permanently delete" : "Restore"} receipt {confirm?.row?.receipt_display}?
            </DialogTitle>
            <DialogDescription>
              {confirm?.kind === "purge"
                ? `${fmtINR(confirm?.row?.amount)} on ${fmtDate(confirm?.row?.payment_date)} for PIN ${confirm?.row?.pin || "—"}. This cannot be undone — the record leaves the municipality's data for good (an archive copy stays in the audit trail).`
                : `${fmtINR(confirm?.row?.amount)} on ${fmtDate(confirm?.row?.payment_date)} for PIN ${confirm?.row?.pin || "—"} comes back exactly as before: registers, totals, the property's balance and the collector's credit.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={busy} data-testid={`${testId}-confirm-cancel`}>Cancel</Button>
            <Button variant={confirm?.kind === "purge" ? "destructive" : "default"} onClick={run} disabled={busy} data-testid={`${testId}-confirm-ok`}>
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : confirm?.kind === "purge" ? <ShieldAlert className="h-4 w-4 mr-1.5" /> : <RotateCcw className="h-4 w-4 mr-1.5" />}
              {busy ? "Working…" : confirm?.kind === "purge" ? "Delete permanently" : "Restore receipt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeletedReceiptsTab;
